import { useState, useEffect } from "react";
import api from "../services/api";

function StarDisplay({ rating }) {
  return (
    <span style={{ color: "#E8622A", fontSize: 13 }}>
      {"★".repeat(rating)}{"☆".repeat(5 - rating)}
    </span>
  );
}

function PropertyCard({ property, onNavigate, badge }) {
  return (
    <button
      onClick={() => onNavigate(property.id)}
      style={{
        width: "100%", textAlign: "left", background: "#fff", border: "1px solid #e0ddd4",
        borderRadius: 10, padding: "14px 18px", cursor: "pointer", display: "block",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <p style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 15, color: "#1a1a2e", margin: "0 0 2px" }}>
            {property.address_line1}
          </p>
          <p style={{ fontSize: 13, color: "#888", margin: 0 }}>
            {property.city}, {property.state} {property.zip_code}
          </p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {property.average_rating > 0 && <StarDisplay rating={Math.round(property.average_rating)} />}
          {property.total_reviews > 0 && (
            <p style={{ fontSize: 11, color: "#aaa", margin: "2px 0 0" }}>{property.total_reviews} review{property.total_reviews !== 1 ? "s" : ""}</p>
          )}
          {badge && <p style={{ fontSize: 11, color: "#E8622A", fontWeight: 600, margin: "4px 0 0" }}>{badge}</p>}
        </div>
      </div>
    </button>
  );
}

export default function AccountPage({ onBack, onNavigateToProperty, onSignOut }) {
  const [tab, setTab] = useState("history");
  const [data, setData] = useState({ history: [], favorites: [], reviews: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAccountSummary()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { key: "history", label: "Searched", count: data.history.length },
    { key: "favorites", label: "Favorited", count: data.favorites.length },
    { key: "reviews", label: "My Reviews", count: data.reviews.length },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f0" }}>
      {/* Header */}
      <header style={{ background: "#1a1a2e", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 16 }}>← Back</button>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
            Door<span style={{ color: "#E8622A" }}>Report</span>
          </span>
        </div>
        <button onClick={onSignOut} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 13 }}>Sign Out</button>
      </header>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 20px 80px" }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 24, color: "#1a1a2e", margin: "0 0 24px" }}>My Account</h1>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: "2px solid #e0ddd4", marginBottom: 24 }}>
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "10px 20px", border: "none", background: "transparent", cursor: "pointer",
              fontSize: 14, fontWeight: 600, color: tab === t.key ? "#E8622A" : "#888",
              borderBottom: `2px solid ${tab === t.key ? "#E8622A" : "transparent"}`,
              marginBottom: -2, whiteSpace: "nowrap",
            }}>
              {t.label}
              {t.count > 0 && (
                <span style={{ marginLeft: 6, background: tab === t.key ? "#E8622A" : "#e0ddd4", color: tab === t.key ? "#fff" : "#888", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: "#888", textAlign: "center", padding: 40 }}>Loading...</p>
        ) : (
          <>
            {tab === "history" && (
              <div>
                {data.history.length === 0 ? (
                  <EmptyState message="No properties searched yet." sub="Properties you view will appear here." />
                ) : (
                  data.history.map((p) => (
                    <PropertyCard key={p.id} property={p} onNavigate={onNavigateToProperty}
                      badge={p.searched_at ? `Viewed ${new Date(p.searched_at).toLocaleDateString()}` : null}
                    />
                  ))
                )}
              </div>
            )}

            {tab === "favorites" && (
              <div>
                {data.favorites.length === 0 ? (
                  <EmptyState message="No favorites yet." sub="Tap the ♡ on any property page to save it here." />
                ) : (
                  data.favorites.map((p) => (
                    <PropertyCard key={p.id} property={p} onNavigate={onNavigateToProperty}
                      badge={p.favorited_at ? `Saved ${new Date(p.favorited_at).toLocaleDateString()}` : null}
                    />
                  ))
                )}
              </div>
            )}

            {tab === "reviews" && (
              <div>
                {data.reviews.length === 0 ? (
                  <EmptyState message="You haven't reviewed anything yet." sub="Visit a property page to leave a review." />
                ) : (
                  data.reviews.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => onNavigateToProperty(r.property_id)}
                      style={{ width: "100%", textAlign: "left", background: "#fff", border: "1px solid #e0ddd4", borderRadius: 10, padding: "14px 18px", cursor: "pointer", marginBottom: 10, display: "block" }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <p style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 15, color: "#1a1a2e", margin: "0 0 2px" }}>{r.title}</p>
                          <p style={{ fontSize: 13, color: "#888", margin: "0 0 4px" }}>{r.address_line1}, {r.city}, {r.state}</p>
                          <StarDisplay rating={r.overall_rating} />
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <p style={{ fontSize: 11, color: "#aaa", margin: 0 }}>{new Date(r.created_at).toLocaleDateString()}</p>
                          <p style={{ fontSize: 11, fontWeight: 600, margin: "4px 0 0", color: r.is_approved ? "#16a34a" : "#d97706" }}>
                            {r.is_approved ? "Published" : "Pending review"}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ message, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "#aaa" }}>
      <p style={{ fontSize: 16, fontFamily: "Georgia, serif", color: "#888", marginBottom: 8 }}>{message}</p>
      <p style={{ fontSize: 13 }}>{sub}</p>
    </div>
  );
}
